import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { DocumentDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import type { DocumentRegisterResponse } from '@risksmart-app/trpc/src/types';
import type {
  Document_Bool_Exp,
  GetDocumentsQuery,
  GetDocumentsQueryVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetDocumentsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import _ from 'lodash';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import { DOCUMENT_DATE_PREDICATES } from '@/hooks/queries/dashboardDatePredicates';
import {
  buildDashboardWhere,
  type DashboardFilterArgs,
  departmentsWhereOnEntity,
  type FullResultHistoryArg,
  type ResultHistoryWindowArg,
  tagsWhereOnEntity,
  toFullResultHistoryVariables,
  toResultHistoryVariables,
  toTrpcDashboardFilterInput,
} from '@/hooks/queries/dashboardFilterArgs';

/**
 * Maps TRPC document data to match the GraphQL query structure
 */
export function mapTrpcDocumentsToGraphQL(
  trpcData: DocumentRegisterResponse[]
): StrictMappedGraphQLSelection<GetDocumentsQuery> {
  const registerData = trpcData[0];
  if (!registerData) {
    return { document: [], assessment_result_parent: [] };
  }

  const documentWithLatestPublishedVersion = registerData.document.map(
    (document) => {
      const previouslyPublishedVersions = document?.documentFiles.filter(
        (documentFile) => !!documentFile.PublishedDate
      );

      const latestPublishedVersion = _.orderBy(
        previouslyPublishedVersions,
        ['PublishedDate'],
        ['desc']
      )[0];

      const latestDocumentFile = _.orderBy(
        document?.documentFiles,
        ['CreatedAtTimestamp'],
        ['desc']
      )[0];

      return {
        ...document,
        documentFiles: [latestDocumentFile],
        latestPublishedVersion: [
          {
            PublishedDate: latestPublishedVersion?.PublishedDate,
          },
        ],
      };
    }
  );

  return {
    document: documentWithLatestPublishedVersion,
    assessment_result_parent: registerData.assessment_result_parent,
  };
}

const usePolicyRegisterGraphqlVariables = (args: UseGetPolicyRegisterArgs) => {
  const { user } = useRisksmartUser();

  // Explicit type arguments: document has no `base` (its own predicate is
  // `undefined`), so there is nothing to anchor `TWhere` for inference — see
  // the obligation hook's identical comment. The third argument
  // (`TDateFragment`) is ALSO required here: document is the one entity whose
  // date map returns more than a bare `where` (`filesWhere`), and supplying
  // only the first two type arguments suppresses inference for the defaulted
  // `TDateFragment` generic, which would silently drop `filesWhere` from
  // `dateVariables`'s type.
  const { where, dateVariables } = buildDashboardWhere<
    DashboardFilterArgs<DocumentDashboardDateField>,
    Document_Bool_Exp,
    Partial<GetDocumentsQueryVariables>
  >({
    filters: args,
    userId: user?.userId,
    datePredicates: DOCUMENT_DATE_PREDICATES,
    tagsWhere: tagsWhereOnEntity,
    departmentsWhere: departmentsWhereOnEntity,
    ownershipWhere: (conditions) => conditions.documentFilterConditions,
  });

  const resultHistoryVariables =
    toResultHistoryVariables<GetDocumentsQueryVariables>(
      args.resultHistoryWindow,
      (bounds) => ({ documentAssessmentResultsWhere: { TestDate: bounds } })
    );

  // COI-838 — OMITTED unless the caller opts out of the cap. The document
  // defaults `$assessmentResultsLimit` to 2, and a GraphQL default applies only
  // to an ABSENT variable — so this must contribute nothing in the common case
  // and `{ assessmentResultsLimit: null }` for the two rating charts.
  const fullResultHistoryVariables =
    toFullResultHistoryVariables<GetDocumentsQueryVariables>(
      args.fullResultHistory,
      () => ({ assessmentResultsLimit: null })
    );

  return {
    ...dateVariables,
    ...resultHistoryVariables,
    ...fullResultHistoryVariables,
    ...(where ? { where } : {}),
  };
};

export type UseGetPolicyRegisterArgs =
  DashboardFilterArgs<DocumentDashboardDateField> &
    ResultHistoryWindowArg &
    FullResultHistoryArg;

export const useGetPolicyRegister = createQueryHook<
  UseGetPolicyRegisterArgs,
  DocumentRegisterResponse[],
  GetDocumentsQuery
>({
  trpcQueryOptions: (
    trpc,
    {
      tagTypeIds,
      departmentTypeIds,
      dateRange,
      ownership,
      resultHistoryWindow,
      fullResultHistory,
    }
  ) =>
    trpc.frontend.document.register.queryOptions(
      toTrpcDashboardFilterInput({
        tagTypeIds,
        departmentTypeIds,
        dateRange,
        ownership,
        resultHistoryWindow,
        fullResultHistory,
      })
    ),
  mapTrpcDataToGraphQL: mapTrpcDocumentsToGraphQL,
  graphqlDocument: GetDocumentsDocument,
  graphqlVariables: usePolicyRegisterGraphqlVariables,
});
